import { isBoolean, isNil, omitBy } from 'es-toolkit';
import EventEmitter from 'eventemitter3';
import { ApsIframe } from './ApsIframe';
import { loadAmazonAPS } from './loadAmazonAPS';
const PUB_ID = '3111b792-be07-4cd2-bd99-c42ac9a74f71';
const AD_SERVER = 'googletag';
const VIDEO_AD_SERVER = 'DFP';
const SLOT_ID = 'APS_VideoINT_GAM';
const VIDEO_MEDIUM = 'video';
const ONE_MINUTE_IN_MILLISECONDS = 60_000;
let isDebugMode = false;
const debug = (message, ...args) => {
    if (isDebugMode) {
        console.log(`[ApsBidder] ${message}`, ...args);
    }
};
export class ApsBidder extends EventEmitter {
    #apsIframeController;
    #options;
    #ready;
    #useApsIframe;
    #consentData;
    #isFetchingVideoBids = false;
    #nextVideoBids;
    #readyVideoBids;
    #videoBidsPollIntervalId;
    #videoBidsReady;
    constructor(options) {
        super();
        this.#options = {
            areVideoBidsEnabled: false,
            videoBidsFetchIntervalMinutes: 60,
            videoBidsFetchTimeoutMs: 3000,
            debug: false,
            ...(options && omitBy(options, isNil)),
        };
        isDebugMode = this.#options.debug || false;
        this.#apsIframeController = new ApsIframe(this.#options.debug);
        this.#ready = Promise.withResolvers();
    }
    #getApsInitParams() {
        if (!this.#consentData) {
            return;
        }
        const { gdpr, ccpa } = this.#consentData;
        const { gdprApplies, tcString } = gdpr || {};
        const { usPrivacyString } = ccpa || {};
        const params = {};
        if (usPrivacyString) {
            params.us_privacy = usPrivacyString;
        }
        if (isBoolean(gdprApplies)) {
            params.gdpr = gdprApplies ? 1 : 0;
        }
        if (tcString) {
            params.gdpr_consent = tcString;
        }
        return {
            pubID: PUB_ID,
            adServer: AD_SERVER,
            videoAdServer: VIDEO_AD_SERVER,
            params,
        };
    }
    async #fetchAndStoreVideoBids() {
        this.#isFetchingVideoBids = true;
        this.#videoBidsReady = Promise.withResolvers();
        setTimeout(this.#videoBidsReady.resolve, this.#options.videoBidsFetchTimeoutMs);
        const videoSlot = {
            slotID: SLOT_ID,
            mediaType: VIDEO_MEDIUM,
        };
        try {
            const bids = (await this.fetchBids(videoSlot));
            this.#nextVideoBids = bids?.length
                ? bids.filter((bid) => bid.mediaType === VIDEO_MEDIUM)
                : [];
            if (!this.#nextVideoBids.length) {
                this.emit('amazonKeywordsRequestFailed', {
                    AD_TYPE: 'AD_TYPE_VIDEO',
                    ErrorCode: '87',
                    ErrorDescription: 'No parameters received.',
                });
            }
            debug('The APS bids request is resolved successfully.');
        }
        catch (ex) {
            this.#nextVideoBids = [];
            this.emit('amazonKeywordsRequestFailed', {
                AD_TYPE: 'AD_TYPE_VIDEO',
                ErrorCode: '85',
                ErrorDescription: 'Amazon keywords request failed.',
            });
            debug('The APS bids request is failed.', ex);
        }
        this.#isFetchingVideoBids = false;
        this.#videoBidsReady.resolve();
    }
    get hasVideoBids() {
        return Boolean(this.#readyVideoBids?.length);
    }
    get videoBidsAdParams() {
        return this.#readyVideoBids
            ?.map((bid) => bid.qsParams || bid.encodedQsParams)
            .join('');
    }
    async init({ scriptUrlOverride, useApsIframe, consentData, }) {
        this.#useApsIframe = useApsIframe;
        this.#consentData = consentData;
        const initParams = this.#getApsInitParams();
        if (!initParams) {
            return;
        }
        if (this.#useApsIframe) {
            this.#apsIframeController.init(initParams, this.#ready.resolve);
        }
        else {
            loadAmazonAPS(scriptUrlOverride);
            window.apstag?.init(initParams, this.#ready.resolve);
        }
        await this.#ready.promise;
        if (this.#options.areVideoBidsEnabled) {
            this.pollAndCacheVideoBids();
        }
    }
    async fetchBids(slot) {
        const bidOptions = {
            slots: [slot],
            timeout: 2e3,
        };
        await this.#ready.promise;
        if (this.#useApsIframe) {
            return this.#apsIframeController.fetchBids(bidOptions);
        }
        return new Promise((resolve) => {
            window.apstag?.fetchBids(bidOptions, (bids) => {
                debug('Received APS bids', bids);
                resolve(bids);
            });
        });
    }
    async pollAndCacheVideoBids() {
        if (!this.#options.areVideoBidsEnabled) {
            return;
        }
        if (this.#isFetchingVideoBids) {
            await this.#videoBidsReady?.promise;
            return;
        }
        if (this.#videoBidsPollIntervalId) {
            clearInterval(this.#videoBidsPollIntervalId);
        }
        await this.#videoBidsReady?.promise;
        this.#readyVideoBids = this.#nextVideoBids || [];
        this.#fetchAndStoreVideoBids();
        this.#videoBidsPollIntervalId = setInterval(async () => {
            await this.#videoBidsReady?.promise;
            this.#fetchAndStoreVideoBids();
        }, this.#options.videoBidsFetchIntervalMinutes *
            ONE_MINUTE_IN_MILLISECONDS);
    }
}
